import { Home, Upload, Mail, AccountCircle, Explore as ExploreIcon } from '@mui/icons-material';
import { Sun, Moon, X, Mail as LucideMail, User } from 'lucide-react';
import { FaPhone } from 'react-icons/fa';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import Logo from '../../assets/Images/Logo.png';
import api from '../../api/axiosConfig';
import Fuse from 'fuse.js';

export default function SidebarDash() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false); 
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [allPropertiesCache, setAllPropertiesCache] = useState([]);

  const { t } = useTranslation('Sidebardash');

  // Email and phone constants
  const COMPANY_EMAIL = 'pongoincb@gmail.com';
  const COMPANY_PHONE = '+250789544277';

  const handleEmailClick = () => {
    window.location.href = `mailto:${COMPANY_EMAIL}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${COMPANY_PHONE.replace(/\s/g, '')}`;
  };

  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setRecaptchaReady(true);
        });
      }
    };

    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);


useEffect(() => {
  const fetchAllPropertiesForSearch = async () => {
    try {
      const response = await api.get('/properties/?limit=100&fields=id,title,location');
      const data = response.data.results || response.data;
      setAllPropertiesCache(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Could not preload properties for fuzzy search', err);
    }
  };

  fetchAllPropertiesForSearch();
}, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!recaptchaReady) {
        alert(t('sidebar.contact.status.securityError'));
        return;
      }

      const recaptchaToken = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'contact' }
      );

      await api.post('/contact/', { ...contactForm, recaptcha_token: recaptchaToken });
      setSubmitStatus('success');
      setTimeout(() => setIsContactOpen(false), 2000);
    } catch (err) {
      console.error('Contact submission failed:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      let cleaned = value.replace(/[^\d+]/g, ''); 
      if (cleaned.startsWith('++')) {
        cleaned = '+' + cleaned.substring(2).replace(/\+/g, '');
      } else if (cleaned.includes('+')) {
        cleaned = '+' + cleaned.replace(/\+/g, '');
      }
      setContactForm((prev) => ({ ...prev, [name]: cleaned }));
    } else {
      setContactForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // SEARCH FUNCTIONALITY
  const debouncedSearch = useCallback((query) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/properties/?search=${encodeURIComponent(query)}`);
        const data = response.data.results || response.data;
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
  if (!query.trim() || query.length < 2) {
    setSuggestions([]);
    return;
  }

  try {
    // First: try backend suggestions
    const res = await api.get(`/search-suggestions/?q=${encodeURIComponent(query)}`);
    const backendSuggestions = res.data;

    // If backend returns suggestions, use them
    if (Array.isArray(backendSuggestions) && backendSuggestions.length > 0) {
      setSuggestions(backendSuggestions);
      return;
    }

    // Fallback: use Fuse.js fuzzy search on cached properties
    if (allPropertiesCache.length > 0) {
      const fuse = new Fuse(allPropertiesCache, {
        keys: ['title', 'location'],
        threshold: 0.3, 
        includeScore: true,
        shouldSort: true,
      });

      const fuseResults = fuse.search(query);
      const fuzzySuggestions = fuseResults
        .slice(0, 5)
        .map((result) => result.item.title || result.item.location);

      setSuggestions(fuzzySuggestions);
    } else {
      setSuggestions([]);
    }
  } catch (err) {
    console.error('Failed to fetch suggestions:', err);

    // On error, try Fuse.js if cache exists
    if (allPropertiesCache.length > 0) {
      const fuse = new Fuse(allPropertiesCache, {
        keys: ['title', 'location'],
        threshold: 0.35,
        includeScore: true,
      });
      const fuseResults = fuse.search(query);
      const fuzzySuggestions = fuseResults
        .slice(0, 5)
        .map((result) => result.item.title || result.item.location);
      setSuggestions(fuzzySuggestions);
    } else {
      setSuggestions([]);
    }
  }
}, [allPropertiesCache]);


  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery);
      fetchSuggestions(searchQuery);
    } else {
      setSearchResults([]);
      setSuggestions([]);
    }
  }, [searchQuery, debouncedSearch, fetchSuggestions]);

  const handleSearchResultClick = useCallback((propertyId) => {
    if (!propertyId) return;
    setIsSearchPanelOpen(false);
    
    const currentPath = location.pathname;
    const targetPath = currentPath.includes('exploredash') ? '/exploredash' : '/homedash';
    
    navigate(`${targetPath}?property_id=${encodeURIComponent(propertyId)}`, {
      state: { scrollToProperty: propertyId }
    });
  }, [location.pathname, navigate]);



  const handleSeeAllResults = () => {
    setIsSearchPanelOpen(false);
    navigate(`/exploredash?search=${encodeURIComponent(searchQuery)}`);
  };

  const mainNavItems = [
    { name: 'Home', path: '/homedash', icon: <Home /> },
    { name: 'Explore', path: '/exploredash', icon: <ExploreIcon /> },
    { name: 'Upload', path: '/uploaddash', icon: <Upload /> },
  ];

  const menuNavItems = [
    { name: 'Messages', path: '/messages', icon: <Mail /> },
    { name: 'Profile', path: '/profiledash', icon: <AccountCircle /> },
    { name: 'Search', action: 'search', icon: <Search size={20} /> },
    { name: 'Contact', action: 'contact', icon: <User size={20} /> },
  ];
  
  const contactItem = { name: 'Contact', icon: <User size={20} /> };
  const isActive = (path) => location.pathname === path;

  // RENDER MOBILE OR DESKTOP UI
  const renderContent = () => {
    if (isMobile) {
      return (
        <>
          {/* Mobile Bottom Nav */}
          <div className="fixed bottom-0 left-0 right-0 z-30 bg-[var(--bg-black)] backdrop-blur-xl border-t border-[var(--border)] px-1 py-1">
            <div className="flex justify-between items-center"> 
              {mainNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center no-underline py-1.5 px-1 text-xs font-medium transition-colors flex-1 min-w-0 ${isActive(item.path) ? 'text-pink-500' : 'text-[var(--text-black)]'}`}
                >
                  <div className="text-lg">{item.icon}</div>
                  <span className="truncate text-center">{t(`sidebar.navigation.${item.name}`)}</span>
                </Link>
              ))}

              <button
                onClick={() => setMobileMenuOpen(true)}
                className="flex flex-col items-center no-underline py-1.5 px-1 text-xs transition-colors text-[var(--text-black)] hover:text-pink-500 bg-transparent border-none flex-1 min-w-0"
                aria-label="More options"
              >
                <div className="text-lg">≡</div>
                <span className="truncate text-center">Menu</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Popup */}
          {mobileMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40 bg-black/50"
                onClick={() => setMobileMenuOpen(false)}
              />
              <div className="fixed bottom-16 left-1/2 transform -translate-x-1/2 z-[9997] bg-[var(--bg-black)] border border-[var(--border)] rounded-xl shadow-lg overflow-hidden min-w-[180px]">
                {menuNavItems.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (item.path) {
                        navigate(item.path);
                      } else if (item.action === 'contact') {
                        setIsContactOpen(true);
                        setContactForm({ name: '', email: '', phone: '', message: '' });
                        setSubmitStatus(null);
                      } else if (item.action === 'search') {
                        setIsSearchPanelOpen(true);
                        setSearchQuery('');
                      }
                    }}
                    className="w-full text-left px-4 py-3 bg-[var(--bg-black)] text-[var(--text-black)] flex items-center gap-3 text-sm font-medium border-white/10 border-solid"
                  >
                    <span className="text-lg">{item.icon}</span>
                    {item.name !== 'Search' && <span>{t(`sidebar.navigation.${item.name}`)}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      );
    }

    // Desktop layout
    return (
      <aside className="sticky top-0 left-0 z-50 h-screen w-32 bg-[var(--bg-black)] border-r border-white/20 flex flex-col py-6 px-4">
        {/* Logo */}
        <div className="flex items-center mb-10">
          <img src={Logo} alt="Pongo Logo" className="h-8 w-auto" />
          <span className="ml-3 text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-pink-500">
            Pongo
          </span>
        </div>

        <button
          onClick={() => setIsSearchPanelOpen(true)}
          className="flex items-center w-36 mb-6 px-3 py-1.5 rounded-full bg-[var(--bg-black)] border border-[var(--border)] hover:bg-white/10 transition text-[var(--text-black)] text-sm"
          aria-label={t('sidebar.search.label')}
        >
          <Search size={20} className="text-[var(--text-black)] mr-2 flex-shrink-0" />
          <span className="truncate opacity-70">
            {t('sidebar.search.placeholder')}
          </span>
        </button>

        {/* Navigation */}
        <nav className="flex flex-col space-y-3 w-full flex-grow">
          {mainNavItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`flex items-center rounded-xl py-1 w-36 no-underline transition-all group
                ${isActive(item.path)
                  ? 'bg-[var(--button-hover)] text-pink-500'
                  : 'text-[var(--text-dark)] hover:bg-[var(--button-hover)]'
                }`}
            >
              <div className="text-xl mr-3">{item.icon}</div>
              <span className="font-medium">{t(`sidebar.navigation.${item.name}`)}</span>
            </Link>
          ))}

          {menuNavItems
            .filter(item => item.path)
            .map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center rounded-xl py-1 w-36 no-underline transition-all group
                  ${isActive(item.path)
                    ? 'bg-[var(--button-hover)] text-pink-500'
                    : 'text-[var(--text-dark)] hover:bg-[var(--button-hover)]'
                  }`}
              >
                <div className="text-xl mr-3">{item.icon}</div>
                <span className="font-medium">{t(`sidebar.navigation.${item.name}`)}</span>
              </Link>
            ))
          }

          <button
            onClick={() => {
              setIsContactOpen(true);
              setContactForm({ name: '', email: '', phone: '', message: '' });
              setSubmitStatus(null);
            }}
            className="flex items-center border-none rounded-xl py-1 w-36 no-underline transition-all group bg-[var(--bg-black)] text-[var(--text-black)] hover:text-pink-500"
          >
            <div className="text-xl mr-3">{contactItem.icon}</div>
            <span className="font-medium">{t('sidebar.navigation.Contact')}</span>
          </button>
        </nav>

        <div className="mt-auto flex flex-col items-start w-full space-y-4">
          <button
            onClick={toggleTheme}
            className="p-2.5 bg-[var(--bg-black)] text-[var(--text-black)] border border-[var(--border)] border-solid z-10 image-nav-button rounded-full flex items-center justify-center"
            title={t('sidebar.theme.toggle')}
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </aside>
    );
  };

  return (
    <>
      {/*  MAIN UI: MOBILE OR DESKTOP */}
      {renderContent()}

      {/*  START: MOVED TO BOTTOM FOR MOBILE SUPPORT — Contact Modal */}
      {isContactOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
           onClick={() => setIsContactOpen(false)}
        >
          <div
            className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[var(--text-black)]">{t('sidebar.contact.title')}</h2>
              <button
                onClick={() => {
                  setIsContactOpen(false);
                  setSubmitStatus(null);
                }}
                className="text-[var(--text-black)] hover:text-pink-500 bg-transparent font-bold border-none"
              >
                <X size={20} />
              </button>
            </div>

            {submitStatus === 'success' ? (
              <div className="text-green-500 text-center py-4">
                {t('sidebar.contact.status.success')}
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder={t('sidebar.contact.form.name')}
                  value={contactForm.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t('sidebar.contact.form.email')}
                  value={contactForm.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('sidebar.contact.form.phone')}
                  value={contactForm.phone}
                  onChange={handleInputChange}
                  inputMode="numeric"       
                  pattern="[0-9+\s\-()]*"     
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <textarea
                  name="message"
                  placeholder={t('sidebar.contact.form.message')}
                  value={contactForm.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                  required
                />

                <button
                  type="submit"
                  disabled={isSubmitting || !recaptchaReady}
                  className="w-full py-2.5 bg-pink-500 border-none text-white rounded-lg font-medium hover:bg-pink-600 transition disabled:opacity-60"
                >
                  {!recaptchaReady
                    ? t('sidebar.contact.buttons.loadingSecurity')
                    : isSubmitting
                      ? t('sidebar.contact.buttons.sending')
                      : t('sidebar.contact.buttons.send')
                  }
                </button>
              </form>
            )}

            {submitStatus === 'error' && (
              <div className="text-red-500 text-sm text-center mt-2">
                {t('sidebar.contact.status.error')}
              </div>
            )}
            <div className="text-center text-xs text-[var(--text-muted)] mt-4 pt-4 border-t border-[var(--border)]">
              <button
                onClick={handlePhoneClick}
                className="flex items-center justify-center gap-1 text-[var(--text-muted)] hover:text-pink-500 transition-colors bg-transparent border-none mx-auto mb-2"
              >
                <FaPhone size={10} />
                <span>{COMPANY_PHONE}</span>
              </button>

              <button
                onClick={handleEmailClick}
                className="flex items-center justify-center gap-1 text-[var(--text-muted)] hover:text-pink-500 transition-colors bg-transparent border-none mx-auto"
              >
                <LucideMail size={14} />
                <span>{COMPANY_EMAIL}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* END: MOVED TO BOTTOM FOR MOBILE SUPPORT — Contact Modal */}

      {/* START: Search Panel — also moved out for consistency */}
      {isSearchPanelOpen && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/50"
            onClick={() => setIsSearchPanelOpen(false)}
          />
          <div
            className="fixed top-0 left-0 z-[9999] w-full max-w-xs sm:w-80 h-screen bg-[var(--bg-black)] border-r border-[var(--border)] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[var(--border)]">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-[var(--text-black)]">{t('sidebar.search.resultsTitle')}</h3>
                <button
                  onClick={() => setIsSearchPanelOpen(false)}
                  className="text-[var(--text-black)] hover:text-pink-500 bg-transparent border-none"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mt-4 relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1.5 -translate-y-1/2 text-[var(--text-black)]" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('sidebar.search.placeholder')}
                    className="w-full pl-10 pr-4 py-2 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1.5 -translate-y-1/2 text-pink-500 bg-transparent border-none"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {searchLoading && !searchQuery ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-500"></div>
              </div>
            ) : suggestions.length > 0 && searchQuery ? (
              <div className="p-4">
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={async () => {
                        setSearchQuery(suggestion);
                        setSuggestions([]); 
                        setSearchLoading(true);
                        
                        try {
                          const res = await api.get(`/properties/?search=${encodeURIComponent(suggestion)}`);
                          const data = res.data.results || res.data;
                          const results = Array.isArray(data) ? data : [];
                          setSearchResults(results);

                          // Auto-navigate if only 1 result
                          if (results.length === 1) {
                            await new Promise(r => setTimeout(r, 300)); 
                            setIsSearchPanelOpen(false);
                            navigate(`/homedash?property_id=${encodeURIComponent(results[0].id)}`);
                          }
                        } catch (err) {
                          console.error('Search from suggestion failed:', err);
                          setSearchResults([]);
                        } finally {
                          setSearchLoading(false);
                        }
                      }}
                      className="w-full text-left px-3 py-2 border-white/10 rounded-lg text-[var(--text-black)] bg-[var(--bg-black)] text-sm"
                    >
                      {suggestion}
                    </button>
                  ))}

                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="p-4 space-y-4">
                {searchResults.slice(0, 5).map((result) => (
                  <div
                    key={result.id}
                    onClick={() => handleSearchResultClick(result.id)}
                    className="p-3 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-lg cursor-pointer hover:bg-[var(--button-hover)] transition"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={result.images?.[0]?.image || '/fallback.jpg'}
                        alt={result.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[var(--text-black)] truncate">{result.title}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{result.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {searchResults.length > 5 && (
                  <button
                    onClick={handleSeeAllResults}
                    className="w-full mt-4 py-2.5 bg-pink-500 border-none text-white rounded-lg font-medium hover:bg-pink-600 transition"
                  >
                    {t('sidebar.search.seeAll')}
                  </button>
                )}
              </div>
            ) : searchQuery ? (
              <div className="text-center py-10 text-[var(--text-black)]">
                {t('sidebar.search.noResults')}
              </div>
            ) : (
              <div className="text-center py-10 text-[var(--text-black)] opacity-60">
                {t('sidebar.search.placeholder')}
              </div>
            )}
          </div>
        </>
      )}
      {/* END: Search Panel */}
    </>
  );
}